import { LoadingSpinner } from "@connected-repo/ui-mui/components/LoadingSpinner";
import { Box } from "@connected-repo/ui-mui/layout/Box";
import { CustomErrorBoundary } from "@frontend/components/error_fallback";
import { RootLayout } from "@frontend/components/layout/RootLayout";
import * as Sentry from "@sentry/react";
import { createBrowserRouter, redirect } from "react-router";
// HydrateFallback component for initial app loading
const HydrateFallback = () => (<Box sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        bgcolor: "background.default",
    }}>
		<LoadingSpinner size={48}/>
	</Box>);
// react-router `lazy` route property: the router awaits the chunk before
// swapping the page, so `useNavigation().state` is "loading" during the fetch
// (which drives the TopProgressBar) instead of React.lazy's blank-Suspense flash.
const lazyRoute = (importer) => async () => {
    const mod = await importer();
    return { Component: mod.default };
};
const routerObjectWithNavbar = [
    {
        path: "/",
        Component: RootLayout,
        errorElement: <CustomErrorBoundary />,
        hydrateFallbackElement: <HydrateFallback />,
        children: [
            {
                index: true,
                loader: () => redirect("/dashboard"),
            },
            {
                path: "auth/*",
                lazy: lazyRoute(() => import("@frontend/modules/auth/auth.router")),
            },
            // Docket import-clearance console — public demo (no auth) that
            // serves dummy data from the backend so the end-to-end flow can be
            // shown to the client for sign-off before the real backend is
            // built. `ConsoleLayout` is a pathless layout route rendering its
            // own sidebar + navbar chrome.
            {
                lazy: lazyRoute(() => import("@frontend/console/components/ConsoleLayout")),
                children: [
                    {
                        path: "dashboard",
                        lazy: lazyRoute(() => import("@frontend/console/pages/Dashboard.page")),
                    },
                    {
                        path: "offers",
                        lazy: lazyRoute(() => import("@frontend/console/pages/DailyOffers.page")),
                    },
                    {
                        path: "position",
                        lazy: lazyRoute(() => import("@frontend/console/pages/Position.page")),
                    },
                    {
                        path: "payments",
                        lazy: lazyRoute(() => import("@frontend/console/pages/PaymentCalendar.page")),
                    },
                    {
                        path: "commissions",
                        lazy: lazyRoute(() => import("@frontend/console/pages/Commissions.page")),
                    },
                    {
                        path: "purchases",
                        lazy: lazyRoute(() => import("@frontend/console/pages/PurchaseContracts.page")),
                    },
                    {
                        path: "sales",
                        lazy: lazyRoute(() => import("@frontend/console/pages/SalesContracts.page")),
                    },
                    {
                        path: "bookings",
                        lazy: lazyRoute(() => import("@frontend/console/pages/FreightBookings.page")),
                    },
                    {
                        path: "masters",
                        lazy: lazyRoute(() => import("@frontend/console/pages/Masters.page")),
                    },
                    {
                        path: "shipments",
                        lazy: lazyRoute(() => import("@frontend/console/pages/Shipments.page")),
                    },
                    {
                        path: "shipments/:id",
                        lazy: lazyRoute(() => import("@frontend/console/pages/ShipmentDetail.page")),
                    },
                    {
                        path: "inbox",
                        lazy: lazyRoute(() => import("@frontend/console/pages/Inbox.page")),
                    },
                    {
                        path: "documents",
                        lazy: lazyRoute(() => import("@frontend/console/pages/Documents.page")),
                    },
                    {
                        path: "escalations",
                        lazy: lazyRoute(() => import("@frontend/console/pages/Escalations.page")),
                    },
                    {
                        path: "security",
                        lazy: lazyRoute(() => import("@frontend/console/pages/Security.page")),
                    },
                    {
                        path: "expenses",
                        lazy: lazyRoute(() => import("@frontend/console/pages/Expenses.page")),
                    },
                    {
                        path: "reports",
                        lazy: lazyRoute(() => import("@frontend/console/pages/Reports.page")),
                    },
                    {
                        path: "contacts",
                        lazy: lazyRoute(() => import("@frontend/console/pages/Contacts.page")),
                    },
                    {
                        path: "sop",
                        lazy: lazyRoute(() => import("@frontend/console/pages/Sop.page")),
                    },
                    {
                        path: "settings",
                        lazy: lazyRoute(() => import("@frontend/console/pages/Settings.page")),
                    },
                ],
            },
            // Any stale/removed path (old journal app, /console/*, etc.) lands
            // back on the console home.
            {
                path: "*",
                loader: () => redirect("/"),
            },
        ],
    },
];
// Call this AFTER Sentry.init()
const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV7(createBrowserRouter);
export const router = sentryCreateBrowserRouter(routerObjectWithNavbar);
